import { useState, useMemo } from 'react';
import { isEmpty } from '../../../../helpers/utils';
import { filtersReducer } from '../../_commons/filtersReducer';
import { useOriginDestination } from '../../_commons/locations';
import { useTravel } from './travel';
import { useDateRange, getDateRange } from '../../_commons/dateRange';
import { useEffectReducer } from 'use-effect-reducer';
import useFlightType from '../../InteractiveMapContainer/filters/useFlightType';
import useFullyVaccinated from '../../InteractiveMapContainer/filters/useVaccinated';

const flightTypeMap = {
  rt: 'ROUND_TRIP',
  ow: 'ONE_WAY'
};

const useFilters = ({
  availableFilters = [],
  filters, //filter settings  config/ui props
  filtersTravelRestriction, //filter settings  config/ui props. Separate prop from integration with InteractiveMap
  initialOrigins,
  initialDestinations,
  originDestinationLogic,
  prePopulation, // priority: 1-departure from prePopulation. 2. departure from popupData
  popupData,
  integrationSettings,
  meta,
  pageContext,
  getAirports,
  isVisible,
  firstLoading,
  setFirstLoading,
  getTravelsRestrictions,
  filterFields
}) => {
  const [fetchData, setFetchData] = useState(true);
  const [isfetchData, setIsFetchData] = useState(true);

  const [state, dispatch] = useEffectReducer(filtersReducer, {
    originDestinationLogic,
    origin: { value: popupData.origin || '' },
    destination: { value: popupData.destination || '' },
    dateRange: getDateRange(prePopulation),
    flightType: popupData.journeyType,
    userInteraction: false,
    fullyVaccinated: popupData.fullyVaccinated,
    noClearDestination: true
  });

  const locations = useOriginDestination({
    dispatch,
    origin: state.origin,
    destination: state.destination,
    showOrigin: true,
    showDestination: true,
    filter: { filterFields, getAirports, prePopulation, integrationSettings, meta, pageContext },
    initialOrigins,
    initialDestinations,
    originDestinationLogic,
    userInteraction: state.userInteraction
  });

  const flightType = useFlightType({
    dispatch,
    value: flightTypeMap[state.flightType] ?? state.flightType,
    initValue: flightTypeMap[prePopulation.flightType] ?? prePopulation.flightType
  });

  const fullyVaccinated = useFullyVaccinated({
    dispatch,
    value: state.fullyVaccinated,
    initValue: prePopulation.fullyVaccinated
  });

  const dateRange = useDateRange({ dispatch, value: state.dateRange });

  const filtersLoading = locations.origin.isLoading || locations.destination.isLoading;

  const [originCountry, iosCountry] = useMemo(() => {
    const { origin } = locations;
    const item = origin.data.filter((e) => e.value === origin?.value);
    if (item.length !== 0) {
      return [item[0]?.country?.name, item[0]?.country?.isoCode];
    }
    return [popupData.originCountry, popupData.originMarket];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations.origin]);

  const { travelsInfo, match, retry } = useTravel({
    selectedOrigin: state.origin,
    selectedDestination: state.destination,
    dateRange: dateRange.value,
    flightType: state.flightType,
    fullyVaccinated: state.fullyVaccinated,
    iosCountry,
    firstLoading,
    isVisible,
    setFirstLoading,
    prePopulation,
    meta,
    pageContext,
    integrationSettings,
    getTravelsRestrictions,
    originDestinationLogic,
    getAirports,
    fetchData,
    setFetchData,
    setIsFetchData
  });
  const callToActionSettings = {
    ...filters.callToActionSettings,
    ...filtersTravelRestriction.callToActionSettings,
    disabled:
      !Boolean(locations.origin.value) ||
      !Boolean(locations.destination.value) ||
      isEmpty(state.dateRange) ||
      fetchData,
    onClick: () => {
      setFetchData(true);
    }
  };

  const originSelected = useMemo(() => {
    return (
      locations.origin &&
      locations.origin.data &&
      locations.origin.data.filter((i) => i.value === state.origin.value)[0]
    );
  }, [locations.origin, state.origin.value]);

  const destinationSelected = useMemo(() => {
    return (
      locations.destination &&
      locations.destination.data &&
      locations.destination.data.filter((i) => i.value === state.destination.value)[0]
    );
  }, [locations.destination, state.destination.value]);

  return {
    filtersState: {
      locations,
      dateRange,
      callToActionSettings,
      flightType,
      fullyVaccinated,
      availableSecondaryFilters: availableFilters,
      isfetchData,
      setIsFetchData
    },
    originSelected,
    destinationSelected,
    filtersLoading,
    dispatch,
    travelsInfo,
    match,
    retry,
    originCountry
  };
};

export default useFilters;
